import React from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import StatusIcon, { STEP_STATUS } from './StatusIcon';
import Tag from './Tag';

const STATUS_TONE_STYLE = {
	success: 'statusSuccess',
	accent: 'statusAccent',
	secondary: 'statusSecondary',
	warning: 'statusWarning',
};

/**
 * StepRow: one step of a step list (start of day, stop steps, end of day).
 * Status circle on the LEFT (SPEC decision 13).
 * Props:
 * - status: 'done' (compact, muted) | 'current' (outlined, bold) | 'todo' | 'blocked' (dashed, locked, not pressable)
 * - title, subtitle
 * - statusText: right-hand text, e.g. "Done", "13 stops", "Reopen"
 * - statusTone: 'success' (default for done) | 'accent' (e.g. Reopen) | 'secondary' | 'warning'
 * - subtitleTone: 'default' | 'warning' (e.g. "At least 1 photo required")
 * - tags: array of strings or Tag props ({ label, tone }) shown under the subtitle
 * - onPress; showChevron (default: pressable and not done)
 * - variant: 'card' (default, filled row) | 'plain' (no background, 40 high, 44 when pressable; e.g. phases inside the Home run card)
 * - accessibilityHint, style
 */
const StepRow = ({
	status = STEP_STATUS.TODO,
	title,
	subtitle,
	statusText,
	statusTone,
	subtitleTone = 'default',
	tags,
	onPress,
	showChevron,
	variant = 'card',
	accessibilityHint,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const isDone = status === STEP_STATUS.DONE;
	const isCurrent = status === STEP_STATUS.CURRENT;
	const isBlocked = status === STEP_STATUS.BLOCKED;
	const isPlain = variant === 'plain';
	const isPressable = Boolean(onPress) && !isBlocked;
	const hasChevron = showChevron ?? (isPressable && !isDone && !isPlain);
	const resolvedStatusTone = statusTone || (isDone ? 'success' : 'secondary');
	const hasDetails = !isDone && (Boolean(subtitle) || Boolean(tags?.length));

	const content = (
		<>
			<StatusIcon status={status} size={isPlain ? 20 : 24} />
			<View style={styles.body}>
				<Text
					style={[
						styles.title,
						isDone && styles.titleDone,
						isCurrent && styles.titleCurrent,
						isBlocked && styles.titleBlocked,
						isPlain && (isCurrent ? styles.titlePlainCurrent : styles.titlePlain),
					]}
				>
					{title}
				</Text>
				{hasDetails && Boolean(subtitle) && (
					<Text
						style={[
							styles.subtitle,
							isCurrent && styles.subtitleCurrent,
							isBlocked && styles.subtitleBlocked,
							subtitleTone === 'warning' && styles.subtitleWarning,
						]}
					>
						{subtitle}
					</Text>
				)}
				{hasDetails && Boolean(tags?.length) && (
					<View style={styles.tags}>
						{tags.map((tag) => {
							const tagProps = typeof tag === 'string' ? { label: tag } : tag;
							return <Tag key={tagProps.label} {...tagProps} />;
						})}
					</View>
				)}
			</View>
			{Boolean(statusText) && (
				<Text
					style={[
						styles.statusText,
						STATUS_TONE_STYLE[resolvedStatusTone] && styles[STATUS_TONE_STYLE[resolvedStatusTone]],
						isCurrent && styles.statusCurrent,
						isPlain && !isDone && !isCurrent && styles.statusPlain,
					]}
				>
					{statusText}
				</Text>
			)}
			{hasChevron && (
				<Feather name="chevron-right" size={22} color={isCurrent ? colors.accent : colors.iconMuted} />
			)}
		</>
	);

	const rowStyle = [
		styles.row,
		hasDetails && !isPlain && styles.rowWithDetails,
		isPlain ? styles.plain : styles[status],
		isPlain && isPressable && styles.plainPressable,
		style,
	];
	const accessibilityLabel = [title, subtitle, statusText].filter(Boolean).join(', ');

	if (!isPressable) {
		return (
			<View
				style={rowStyle}
				accessible={true}
				accessibilityLabel={accessibilityLabel}
				accessibilityState={{ disabled: isBlocked }}
			>
				{content}
			</View>
		);
	}

	return (
		<Pressable
			onPress={onPress}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel}
			accessibilityHint={accessibilityHint}
			style={({ pressed }) => [rowStyle, pressed && styles.pressed]}
		>
			{content}
		</Pressable>
	);
};

const createStyles = ({ ui: { colors, radius, touch, type, space } }) => ({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: touch.row,
		paddingHorizontal: 14,
		paddingVertical: space.sm,
		borderRadius: radius.card,
		borderWidth: 1,
	},
	rowWithDetails: {
		minHeight: touch.rowWithSubtitle,
		paddingVertical: 10,
	},
	plain: {
		minHeight: 40,
		paddingHorizontal: 0,
		paddingVertical: space.xs,
		borderWidth: 0,
		borderRadius: 0,
	},
	plainPressable: {
		minHeight: touch.min,
	},
	done: {
		backgroundColor: colors.card,
		borderColor: colors.card,
	},
	todo: {
		backgroundColor: colors.raised,
		borderColor: colors.raisedBorder,
	},
	current: {
		minHeight: touch.currentRow,
		backgroundColor: colors.current,
		borderColor: colors.currentBorder,
		borderWidth: 2,
	},
	blocked: {
		backgroundColor: colors.blockedBg,
		borderColor: colors.blockedBorder,
		borderStyle: 'dashed',
	},
	pressed: {
		opacity: 0.85,
	},
	body: {
		flex: 1,
		gap: 3,
	},
	title: {
		...type.bodyStrong,
		color: colors.text,
	},
	titlePlain: {
		...type.bodySmall,
		color: colors.currentSubtle,
	},
	titleDone: {
		...type.rowText,
		color: colors.textSecondary,
	},
	titleCurrent: {
		...type.sectionTitle,
		color: colors.text,
	},
	titlePlainCurrent: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	titleBlocked: {
		color: colors.blockedText,
	},
	subtitle: {
		...type.secondary,
		color: colors.textSecondary,
	},
	subtitleCurrent: {
		color: colors.currentSubtle,
	},
	subtitleBlocked: {
		color: colors.textFaint,
	},
	subtitleWarning: {
		color: colors.warning,
	},
	tags: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 6,
		marginTop: 2,
	},
	statusText: {
		...type.secondaryStrong,
	},
	statusSuccess: {
		color: colors.success,
	},
	statusAccent: {
		color: colors.accent,
	},
	statusSecondary: {
		color: colors.textSecondary,
	},
	statusWarning: {
		color: colors.warning,
	},
	statusCurrent: {
		...type.secondarySemibold,
		color: colors.accent,
	},
	statusPlain: {
		...type.secondary,
		color: colors.currentSubtle,
	},
});

export default StepRow;
